<template>
  <article class="blog-article post-cell py-xl">
    <div class="mb-base text-block">{{formattedDate}}</div>
    <h2 class="mb-base text-block">{{post.title}}</h2>
    <div class="gallery flex flex-wrap">
      <attachments-view :attachments="attachments"/>
    </div>
    <div v-if="content" v-html="content" class="text-block mt-base w-text"/>
  </article>
</template>

<script>
import * as date from '../../lib/date'
import DOMPurify from 'dompurify'
import AttachmentsView from './AttachmentsView'

export default {
  name: 'BlogArticle',
  components: { AttachmentsView },
  props: {
    post: { type: Object, required: true },
    preloadAttachments: Boolean
  },

  data: () => ({}),

  computed: {
    formattedDate () {
      return date.format(this.post.date, 'long', 'de')
    },
    attachments () {
      return (this.post || {}).attachments || {}
    },

    content () {
      if ((this.post || {}).content) {
        let html = this.post.content
        // Convert video file links to video players
        html = this.convertVideoLinks(html)
        // Configure DOMPurify to allow video elements
        const config = {
          ADD_TAGS: ['video', 'source'],
          ADD_ATTR: ['controls', 'preload', 'poster', 'playsinline', 'muted', 'loop', 'autoplay', 'src', 'type']
        }
        return DOMPurify.sanitize(html, config)
      }
      return ''
    }
  },

  mounted () {

  },

  methods: {
    convertVideoLinks (html) {
      // Match links to video files (.mov, .mp4, .webm, .ogg, etc.)
      const videoExtensions = /\.(mov|mp4|webm|ogg|ogv|avi|wmv|flv|mkv)(\?.*)?$/i
      // Match anchor tags with video file hrefs - handles any attribute order
      const linkRegex = /<a\s+[^>]*href=["']([^"']+\.(mov|mp4|webm|ogg|ogv|avi|wmv|flv|mkv)(\?[^"']*)?)["'][^>]*>([^<]*)<\/a>/gi

      return html.replace(linkRegex, (match, videoUrl, extension, query, linkText) => {
        // Check if the URL is actually a video file
        if (videoExtensions.test(videoUrl)) {
          // Escape the video URL for safe HTML insertion
          const escapedUrl = videoUrl.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
          // Replace the link with a video element
          return `<video controls preload="metadata" style="width: 100%; max-width: 100%; height: auto; margin: 1em 0;">
  <source src="${escapedUrl}" type="video/${this.getVideoMimeType(extension)}">
  Your browser does not support the video tag. <a href="${escapedUrl}">Download the video</a> instead.
</video>`
        }
        // Return original match if not a video file
        return match
      })
    },

    getVideoMimeType (extension) {
      const mimeTypes = {
        mov: 'quicktime',
        mp4: 'mp4',
        webm: 'webm',
        ogg: 'ogg',
        ogv: 'ogg',
        avi: 'x-msvideo',
        wmv: 'x-ms-wmv',
        flv: 'x-flv',
        mkv: 'x-matroska'
      }
      return mimeTypes[extension.toLowerCase()] || 'mp4'
    }
  }
}
</script>

<style lang="scss">
  @import "../../styles/vars";

  .blog-article .attachment-box img {
    max-height: calc(100vh - #{$base-padding} * 2);
    /*max-width: calc(100vh - #{$base-padding} * 2);*/
    width: auto;
    border: none !important;
    border-top: none !important;
    outline: none !important;
    box-shadow: none !important;
    display: block;
  }

  @media (max-width: 30rem) {
    .blog-article .attachment-box img {
      max-height: calc(100vh - #{$small-padding} * 2);
      max-width: 100%;
    }
  }
</style>
<!--suppress CssInvalidAtRule -->
<style lang="scss">
  @import "../../styles/vars";
  @import "../../styles/mixins";

  .blog-article {
    overflow-wrap: break-word;
    word-wrap: break-word;
    overflow-x: hidden;
    width: 100%;
    max-width: 100%;

    @media (max-width: $max-width-phone) {
      overflow-x: hidden !important;
      width: 100% !important;
      max-width: 100% !important;
    }
    -webkit-hyphens: auto;
    -ms-hyphens: auto;
    hyphens: auto;

    & > *:not(.gallery) {
      /*@apply px-sm;*/
    }

    h2 {
      @media (max-width: $max-width-phone) {
        font-size: 1rem !important;
        line-height: 1.2;
        margin-bottom: $small-padding;
      }
    }

    .gallery {
      border: none !important;
      border-top: none !important;
      box-shadow: none !important;
    }
    .attachment {
      border: none !important;
      border-top: none !important;
      box-shadow: none !important;
      &::before,
      &::after {
        display: none !important;
        border: none !important;
      }
    }
    .attachment-box{
      border: none !important;
      border-top: none !important;
      box-shadow: none !important;
      &::before,
      &::after {
        display: none !important;
        border: none !important;
      }
      /*@apply mx-sm;*/
    }

    video {
      width: 100%;
      max-width: 100%;
      height: auto;
      margin: 1em 0;
      border-radius: 4px;
      background: #000;

      @media (max-width: $max-width-phone) {
        margin: $small-padding 0;
      }
    }

    .text-block {
      @media (max-width: $max-width-phone) {
        font-size: 0.75rem;
        line-height: 1.4;
        width: 100%;
        max-width: 100%;
        overflow-wrap: break-word;
        word-wrap: break-word;
        overflow-x: hidden;
      }
    }

    @media (max-width: $max-width-phone) {
      padding-top: $small-padding;
      padding-bottom: $small-padding;
      font-size: 0.8rem;

      & > * {
        margin-bottom: $small-padding;
      }

      .text-block:first-child {
        font-size: 0.65rem;
        margin-bottom: $small-padding;
      }

      h2 {
        font-size: 0.95rem !important;
        line-height: 1.2;
      }
    }
  }
</style>
